import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import useReport from "../../utils/hooks/reports/useReport";
import usePageTitle from "../../utils/hooks/usePageTitle";
import ReportEditorLayout from "../../layouts/ReportEditorLayout";
import { ReportUpdateFields, ReportState, VULNERABILITY_CATEGORIES, VulnerabilityCategory } from "../../utils/api/endpoints/reports/reports";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";
import EditorSection from "../../components/editor/EditorSection";
import EditorTextInput from "../../components/editor/inputs/EditorTextInput";
import ControlledSeverityScoreSelector from "../../components/forms/inputs/ControlledSeverityScoreSelector";
import EditorRichTextInput from "../../components/editor/inputs/EditorRichTextInput";
import EditorAttachmentsInput from "../../components/editor/inputs/EditorAttachmentsInput";
import ReportProgramSelector from "../../components/editor/inputs/reports/ReportProgramSelector";
import RunSelector from "../../components/editor/inputs/reports/RunSelector";
import ReportScopeSelector from "../../components/editor/inputs/reports/ReportScopeSelector";
import { FieldValues, UseFormSetError, useFormContext } from "react-hook-form";
import CategorySelector from "../../components/editor/inputs/reports/CategorySelector";
import HierarchicalCategorySelector from "../../components/editor/inputs/reports/CategorySelector";
import ReportDevAutofill from "../../components/reports/ReportDevAutofill";
import { findPathByLegacyId } from "../../utils/categoryUtils";

const CategoryTaxonomyLabels = () => {
  const { watch } = useFormContext();
  const categoryValue = watch("category");

  const resolvedInfo = useMemo(() => {
    if (!categoryValue) return null;

    try {
      const parsed = JSON.parse(categoryValue);
      const { categoryId, subcategoryId, variantId } = parsed;

      const targetId = variantId || subcategoryId || categoryId;
      if (!targetId) return null;

      const path = findPathByLegacyId(VULNERABILITY_CATEGORIES, targetId);
      if (!path || path.length === 0) return null;

      let owaspCategoryName = "";
      let owaspId = "";
      let cweList: Array<{ id: string; name: string }> = [];

      for (const node of path) {
        if (node.owasp_id) {
          owaspId = node.owasp_id;
          owaspCategoryName = node.name;
        }
        if (node.cwe) {
          if (Array.isArray(node.cwe)) {
            cweList = node.cwe;
          } else if (typeof node.cwe === "string") {
            cweList = [{ id: node.cwe, name: "" }];
          }
        }
      }

      return {
        owaspId,
        owaspCategoryName,
        cweList
      };
    } catch (e) {
      return null;
    }
  }, [categoryValue]);

  if (!resolvedInfo || (!resolvedInfo.owaspId && resolvedInfo.cweList.length === 0)) {
    return null;
  }

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {resolvedInfo.owaspId && (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-700/10">
          <span className="h-1.5 w-1.5 rounded-full bg-indigo-500"></span>
          OWASP: {resolvedInfo.owaspId} ({resolvedInfo.owaspCategoryName})
        </span>
      )}
      {resolvedInfo.cweList.map(cwe => (
        <span
          key={cwe.id}
          title={cwe.name}
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-700/10 cursor-help"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
          {cwe.id}: {cwe.name}
        </span>
      ))}
    </div>
  );
};

const ReportEditor = () => {
  const { id, programId } = useParams();
  const navigate = useNavigate();
  const { role } = useUserCredentials();
  const { report, updateReport, submitReport } = useReport(
    id !== undefined ? parseInt(id) : undefined
  );
  const urlProgramId = programId ? parseInt(programId) : undefined;
  usePageTitle(id ? "Editing Report" : "Create Report");

  // Add loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine if this is a new report or an existing one being edited
  const isNewReport = id === undefined;

  // Stable default values initialization to avoid typing reset issues during silent save transition
  const [stableDefaultValues, setStableDefaultValues] = useState<ReportUpdateFields | null>(null);
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const prevIdRef = useRef(id);
  if (prevIdRef.current !== id) {
    if (prevIdRef.current !== undefined || id === undefined) {
      setStableDefaultValues(null);
      setIsFormInitialized(false);
    }
    prevIdRef.current = id;
  }

  useEffect(() => {
    if (stableDefaultValues) return;

    if (isNewReport) {
      setStableDefaultValues({
        title: "",
        programId: urlProgramId,
        runId: undefined,
        scope: "",
        category: "",
        severity: undefined,
        description: "",
        instructions: "",
        impact: "",
        fix: "",
        additionalInfo: "",
        attachments: undefined,
        assetId: undefined,
        targetIds: [],
        endpointIds: [],
        userId: 0,
        isDelete: false,
        isArchived: false,
        certificateGenerated: false
      } as unknown as ReportUpdateFields);
      setIsFormInitialized(true);
    } else if (report) {
      setStableDefaultValues({
        title: report.title || "",
        programId: report.programId || urlProgramId,
        runId: report.runId,
        scope: report.scope || "",
        category: report.category || "",
        severity: report.severity,
        description: report.description || "",
        instructions: report.instructions || "",
        impact: report.impact || "",
        fix: report.fix || "",
        additionalInfo: report.additionalInfo || "",
        attachments: undefined,
        assetId: report.assetId,
        targetIds: report.targetIds || [],
        endpointIds: report.endpointIds || [],
        userId: report.userId || 0,
        isDelete: report.isDelete || false,
        isArchived: report.isArchived || false,
        certificateGenerated: report.certificateGenerated || false
      } as unknown as ReportUpdateFields);
      setIsFormInitialized(true);
    }
  }, [report, isNewReport, urlProgramId, stableDefaultValues]);

  const saveReport = async <T extends FieldValues>(
    values: ReportUpdateFields,
    setError?: UseFormSetError<T>
  ) => {
    let hasError = false;
    const isSubmittingFinal = !values.isDraft;

    // All reports need at least a title and program id - even if saving as a draft
    if (setError) {
      if (values.title === undefined || values.title === "") {
        setError("title" as any, { type: "required", message: "Title is required." });
        hasError = true;
      }
      if (values.programId === undefined || !values.programId || values.programId === 0) {
        setError("programId" as any, { type: "required", message: "Program is required." });
        hasError = true;
      }

      if (isSubmittingFinal) {
        if (values.scope === undefined || values.scope === "") {
          setError("scope" as any, { type: "required", message: "Scope is required." });
          hasError = true;
        }
        if (values.category === undefined || values.category === "") {
          setError("category" as any, { type: "required", message: "Category is required." });
          hasError = true;
        }
        if (values.severity === undefined || !values.severity || values.severity.score === "-.-" || values.severity.category === "Draft") {
          setError("severity" as any, { type: "required", message: "Severity is required." });
          hasError = true;
        }
        if (values.description === undefined || values.description === "" || values.description === "<p><br></p>") {
          setError("description" as any, { type: "required", message: "Vulnerability details are required." });
          hasError = true;
        }
        if (values.instructions === undefined || values.instructions === "" || values.instructions === "<p><br></p>") {
          setError("instructions" as any, { type: "required", message: "Validation steps are required." });
          hasError = true;
        }
        if (values.impact === undefined || values.impact === "" || values.impact === "<p><br></p>") {
          setError("impact" as any, { type: "required", message: "Impact description is required." });
          hasError = true;
        }
        if (values.fix === undefined || values.fix === "" || values.fix === "<p><br></p>") {
          setError("fix" as any, { type: "required", message: "Recommended fix is required." });
          hasError = true;
        }
      }
    } else {
      if (values.title === undefined || values.title === "" || values.programId === undefined || !values.programId || values.programId === 0) {
        hasError = true;
      }
      if (isSubmittingFinal) {
        if (
          !values.scope ||
          !values.category ||
          !values.severity || values.severity.score === "-.-" || values.severity.category === "Draft" ||
          !values.description || values.description === "<p><br></p>" ||
          !values.instructions || values.instructions === "<p><br></p>" ||
          !values.impact || values.impact === "<p><br></p>" ||
          !values.fix || values.fix === "<p><br></p>"
        ) {
          hasError = true;
        }
      }
    }

    if (hasError) {
      if (isSubmittingFinal) {
        toast.error("Please fill in all required fields before submitting the report.");
      } else {
        toast.error("Please select a Program and enter a Report Title before saving.");
      }
      return 0;
    }

    // Return the result of updating the report (the ID) so that
    // the report can be submitted immediately after saving
    return await updateReport(values, values.attachments);
  };

  // Define actions based on whether this is a new report or an edit
  // Use useMemo to recreate the actions array when isSubmitting changes
  const editorActions = useMemo(() => {
    const actions: Array<{
      label: string;
      onClick?: (values: any, setError: any) => Promise<void>;
      disabled: boolean;
      type?: "submit";
    }> = [];

    const isResearcher = role === UserRole.RESEARCHER;
    const isDraft = report?.state === null;

    if (isNewReport) {
      if (isResearcher) {
        actions.push({
          label: "Save as Draft",
          onClick: async (values, setError) => {
            setIsSubmitting(true);
            try {
              await saveReport({ ...values, isDraft: true }, setError);
            } finally {
              setIsSubmitting(false);
            }
          },
          disabled: isSubmitting
        });

        actions.push({
          label: "Save and Submit",
          onClick: async (values, setError) => {
            setIsSubmitting(true);
            try {
              const savedId = await saveReport({ ...values, isDraft: false }, setError);
              if (savedId) {
                await submitReport(savedId);
              }
            } finally {
              setIsSubmitting(false);
            }
          },
          disabled: isSubmitting
        });
      } else {
        actions.push({
          label: "Save and Submit",
          type: "submit",
          disabled: isSubmitting
        });
      }
    } else {
      if (isResearcher && isDraft) {
        actions.push({
          label: "Save Draft",
          onClick: async (values, setError) => {
            setIsSubmitting(true);
            try {
              await saveReport({ ...values, isDraft: true }, setError);
            } finally {
              setIsSubmitting(false);
            }
          },
          disabled: isSubmitting
        });

        actions.push({
          label: "Submit",
          onClick: async (values, setError) => {
            setIsSubmitting(true);
            try {
              const savedId = await saveReport({ ...values, isDraft: false }, setError);
              if (savedId) {
                await submitReport(savedId);
                navigate(`/dashboard/reports/${savedId}`);
              }
            } finally {
              setIsSubmitting(false);
            }
          },
          disabled: isSubmitting
        });
      } else {
        actions.push({
          label: "Save",
          onClick: async (values, setError) => {
            setIsSubmitting(true);
            try {
              await saveReport(values, setError);
            } finally {
              setIsSubmitting(false);
            }
          },
          disabled: isSubmitting
        });

        const isRejected = report?.state === ReportState.TRIAGE_REJECTED || report?.state === ReportState.BUSINESS_REJECTED;

        if (isResearcher && isRejected) {
          actions.push({
            label: "Save and Resubmit",
            onClick: async (values, setError) => {
              setIsSubmitting(true);
              try {
                const savedId = await saveReport(values, setError);
                if (savedId) {
                  await submitReport(savedId);
                }
              } finally {
                setIsSubmitting(false);
              }
            },
            disabled: isSubmitting
          });
        }
      }
    }

    return actions;
  }, [isNewReport, isSubmitting, role, report, navigate]);

  if (!stableDefaultValues) {
    return (
      <div className="flex h-screen items-center justify-center bg-ctb-grey-250">
        <div className="flex flex-col items-center gap-4">
          <svg className="animate-spin h-10 w-10 text-ctb-green-400" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-ctb-blue-50 font-medium animate-pulse">Loading report editor...</span>
        </div>
      </div>
    );
  }

  const isResearcher = role === UserRole.RESEARCHER;
  const isApprovedOrRequestInfo =
    report &&
    (report.state === ReportState.BUSINESS_REQUEST_INFO ||
      (report.state !== null &&
        report.state !== ReportState.UNDER_REVIEW &&
        report.state !== ReportState.TRIAGE_REJECTED &&
        report.state !== ReportState.BUSINESS_REJECTED));
  const isSeverityDisabled = !!(isResearcher && isApprovedOrRequestInfo);

  return (
    <ReportEditorLayout
      defaultValues={stableDefaultValues}
      actions={editorActions}
      onSubmit={async values => {
        // Only process submission for new reports
        if (isNewReport) {
          setIsSubmitting(true);
          try {
            // Save the report first
            const reportId = await saveReport(values);
            await submitReport(reportId);
          } finally {
            setIsSubmitting(false);
          }
        }
      }}
    >
      <ReportDevAutofill />
      <EditorSection
        sectionTitle={
          id === undefined ? "Create Bug Report" : "Edit Bug Report"
        }
      >
        <EditorTextInput
          name="title"
          inputTitle="Report Title"
          details="A short description of the vulnerability and the affected asset"
          placeholder="Report title..."
          required
        />
        <ReportProgramSelector
          name="programId"
          inputTitle="Program"
          details="Select the program this report belongs to."
          required
        />
        <RunSelector
          name="runId"
          inputTitle="Program Run"
          details="Select the active program run for this report."
          required
        />
        <ReportScopeSelector
          name="scope"
          inputTitle="Scope"
          details="Select the asset, target, and endpoints affected by this vulnerability."
          required
        />
        <HierarchicalCategorySelector
          name="category"
          inputTitle="Vulnerability Category"
          details="Select the type of vulnerability you have discovered."
          required
        />
        <CategoryTaxonomyLabels />
        <ControlledSeverityScoreSelector
          name="severity"
          inputTitle="Severity Score"
          required
          disabled={isSeverityDisabled}
        />
        <EditorRichTextInput
          name="description"
          inputTitle="Vulnerability Details"
          details="Write a description of the vulnerability"
          required
        />
        <EditorRichTextInput
          name="instructions"
          inputTitle="Validation Steps"
          details="Detailed steps on how to exploit/reproduce the vulnerbility."
          required
        />
        <EditorRichTextInput
          name="impact"
          inputTitle="Impact"
          details="Describe the impact of the issue"
          required
        />
        <EditorRichTextInput
          name="fix"
          inputTitle="Recommended Fix"
          details="Describe a possible solution for the business so that they can quickly prevent this problem."
          required
        />
        <EditorRichTextInput
          name="additionalInfo"
          inputTitle="Additional Information"
          details="List any additional information thats relevant to this report."
        />
        <EditorAttachmentsInput
          name="attachments"
          inputTitle="Attachment(s)"
          details="Upload any additional attachments required."
          multiple
        />
      </EditorSection>
      <ReportAutosaveManager
        updateReport={updateReport}
        isNewReport={isNewReport}
        isResearcher={role === UserRole.RESEARCHER}
        isDraft={report?.state === null}
        isRejected={report?.state === ReportState.TRIAGE_REJECTED || report?.state === ReportState.BUSINESS_REJECTED}
      />
    </ReportEditorLayout>
  );
};

const ReportAutosaveManager = ({
  updateReport,
  isNewReport,
  isResearcher,
  isDraft,
  isRejected
}: {
  updateReport: any;
  isNewReport: boolean;
  isResearcher: boolean;
  isDraft: boolean;
  isRejected: boolean;
}) => {
  const { watch } = useFormContext();
  const values = watch();
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const lastSavedValuesRef = useRef<string>("");

  useEffect(() => {
    if (!isResearcher || (!isNewReport && !isDraft && !isRejected)) {
      return;
    }

    if (!values.title || !values.programId || values.programId === 0) {
      return;
    }

    // Omit fields like attachments that shouldn't trigger autosaves
    const { attachments, ...serializableValues } = values;
    const serialized = JSON.stringify(serializableValues);

    if (serialized === lastSavedValuesRef.current) {
      return;
    }

    // Do not trigger immediate autosave on mount for existing drafts/rejected reports
    if (!lastSavedValuesRef.current && !isNewReport) {
      lastSavedValuesRef.current = serialized;
      return;
    }

    setSaveStatus("saving");

    const timer = setTimeout(async () => {
      try {
        await updateReport({ ...values, isDraft: true }, undefined, true);
        lastSavedValuesRef.current = serialized;
        setSaveStatus("saved");
      } catch (err) {
        setSaveStatus("error");
      }
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [values, isNewReport, isResearcher, isDraft, isRejected, updateReport]);

  const hasMinRequirements = !!(values.title && values.programId && values.programId !== 0);

  if (!isResearcher || (!isNewReport && !isDraft && !isRejected)) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-xs font-semibold text-slate-100 shadow-lg border border-slate-700 transition-all animate-pulse">
      {saveStatus === "saving" && (
        <>
          <svg className="animate-spin h-4 w-4 text-ctb-green-400" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span>Saving draft...</span>
        </>
      )}
      {saveStatus === "saved" && (
        <>
          <svg className="h-4 w-4 text-ctb-green-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>Draft saved</span>
        </>
      )}
      {saveStatus === "error" && (
        <>
          <svg className="h-4 w-4 text-ctb-red-500" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="text-ctb-red-400">Failed to autosave</span>
        </>
      )}
      {saveStatus === "idle" && (
        <span>{hasMinRequirements ? "Draft ready" : "Waiting for Title & Program..."}</span>
      )}
    </div>
  );
};

export default ReportEditor;